import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { Channel as ChannelInstance, ConnectionOpen, StreamChat, UserResponse } from 'stream-chat'

import { useChatClient } from '../hooks/use-chat-client'
import { useChatRuntimeConfig } from '../runtime/chat-runtime-config'
import type { IChatCurrentUser, IChatWorkspaceMember } from '../runtime/types'
import type { IChatUser } from '../types/chat-types'
import { useChatConnection } from './__hooks/use-chat-connection'
import { useChatUserSearch } from './__hooks/use-chat-user-search'
import { createGenericContext } from './generic-context'

type TConnectionStatus = 'connected' | 'connecting' | 'disconnected' | 'error'
type QueryChannelsArgs = Parameters<StreamChat['queryChannels']>
type QueryChannelsResult = ReturnType<StreamChat['queryChannels']>

const QUERY_CHANNELS_504_BASE_DELAY_MS = 300
const MAX_QUERY_CHANNELS_504_RETRIES = 3
const STREAM_INBOX_CHANNEL_LIST_MESSAGE_LIMIT = 1
const STREAM_INBOX_CHANNEL_LIST_MEMBER_LIMIT = 2

interface IErrorWithStatusCode {
  code?: number | string
  message?: string
  response?: { status?: number }
  status?: number
}

const sleep = async (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const isInboxChannelListQuery = (...args: QueryChannelsArgs): boolean => {
  const options = args[2]

  return (
    options?.message_limit === STREAM_INBOX_CHANNEL_LIST_MESSAGE_LIMIT &&
    options?.member_limit === STREAM_INBOX_CHANNEL_LIST_MEMBER_LIMIT
  )
}

const isValid504Error = (error: unknown): boolean => {
  const errorObject = (error as IErrorWithStatusCode | undefined) || {}
  const message = errorObject?.message || ''
  const code = errorObject?.code
  const status = errorObject?.status || errorObject?.response?.status

  return message.includes('HTTP code: 504') || code === 504 || code === '504' || status === 504
}

interface IChatContext {
  activeChatID: string | null
  chatClient: StreamChat
  connectionStatus: TConnectionStatus
  currentUser: IChatCurrentUser | null
  disconnectUser: () => void
  hasResolvedInitialInboxChannelQuery: boolean
  isLoading: boolean
  mentionableUsers: UserResponse[]
  searchUser: (inputValue: string) => Promise<IChatUser[] | []>
  teamMembers: IChatWorkspaceMember[]
  onSetActiveChat: (channel: ChannelInstance | null) => void
  unreadCount: number
  userConnection: ConnectionOpen | null
  workspaceUsers: (UserResponse & IChatUser)[]
}

const [useChat, ChatContextProvider] = createGenericContext<IChatContext>(
  'useChat must be used within the ChatContextProvider'
)

interface ChatProviderProps {
  children: React.ReactNode
}

/**
 * ChatProvider wires together the Stream Chat client, connection lifecycle,
 * workspace user list, and user search into a single context consumed by all
 * chat-aware components in the tree.
 *
 * Responsibilities:
 * - Bootstraps the StreamChat client via `useChatClient`
 * - Delegates connection management (connect / disconnect / reconnect) to `useChatConnection`
 * - Resolves mentionable workspace users via `useWorkspaceUsers`
 * - Provides a `searchUser` helper for @-mention search via `useChatUserSearch`
 * - Attaches and cleans up global client event listeners on mount/unmount
 */
const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const {
    telemetry,
    currentUser,
    isLoadingMembers = false,
    mentionableUsers = [],
    searchUser: searchUserFromConfig,
    teamMembers = [],
    userChatToken
  } = useChatRuntimeConfig()
  const {
    client: chatClientService,
    addListeners,
    removeListeners,
    activeChatID,
    setActiveChat: onSetActiveChat
  } = useChatClient()
  const originalQueryChannelsRef = useRef<StreamChat['queryChannels'] | null>(null)
  const hasResolvedInitialInboxChannelQueryRef = useRef(false)
  const userIDRef = useRef<string | undefined>(currentUser?.id)
  const [hasResolvedInitialInboxChannelQuery, setHasResolvedInitialInboxChannelQuery] = useState(false)
  userIDRef.current = currentUser?.id

  const { connectionStatus, unreadCount, userConnection, disconnectUser } = useChatConnection(
    chatClientService,
    currentUser,
    userChatToken ?? null
  )

  const { searchUser: internalSearchUser } = useChatUserSearch(chatClientService, currentUser)
  const searchUser = searchUserFromConfig ?? internalSearchUser
  const isLoading = isLoadingMembers

  // Attach global Stream Chat client listeners once on mount (message.new, notifications,
  // storage sync, beforeunload). Cleaned up on unmount to prevent memory leaks.
  useEffect(() => {
    addListeners()
    return () => removeListeners()
  }, [addListeners, removeListeners])

  useEffect(() => {
    if (originalQueryChannelsRef.current) return

    const originalQueryChannels = chatClientService.queryChannels.bind(chatClientService)
    originalQueryChannelsRef.current = originalQueryChannels as StreamChat['queryChannels']

    const wrappedQueryChannels = (async (...args: QueryChannelsArgs): QueryChannelsResult => {
      const isInitialInboxChannelQuery =
        isInboxChannelListQuery(...args) && !hasResolvedInitialInboxChannelQueryRef.current

      let retryAttempt = 0

      while (true) {
        try {
          const channels = await originalQueryChannels(...args)

          if (isInitialInboxChannelQuery) {
            hasResolvedInitialInboxChannelQueryRef.current = true
            setHasResolvedInitialInboxChannelQuery(true)
          }

          return await (channels as unknown as QueryChannelsResult)
        } catch (error) {
          if (!isValid504Error(error) || retryAttempt >= MAX_QUERY_CHANNELS_504_RETRIES) {
            if (isValid504Error(error) && retryAttempt >= MAX_QUERY_CHANNELS_504_RETRIES) {
              telemetry.captureMessage?.('Stream queryChannels HTTP 504 retry exhausted', {
                extra: {
                  maxRetries: MAX_QUERY_CHANNELS_504_RETRIES,
                  userID: userIDRef.current || 'unknown'
                },
                level: 'error',
                tags: {
                  component: 'ChatProvider',
                  operation: 'queryChannels'
                }
              })
            }

            if (isInitialInboxChannelQuery) {
              hasResolvedInitialInboxChannelQueryRef.current = true
              setHasResolvedInitialInboxChannelQuery(true)
            }

            throw error
          }

          retryAttempt += 1
          const retryDelayMs = QUERY_CHANNELS_504_BASE_DELAY_MS * Math.pow(2, retryAttempt - 1)

          telemetry.addBreadcrumb?.({
            category: 'chat',
            data: {
              maxRetries: MAX_QUERY_CHANNELS_504_RETRIES,
              retryAttempt,
              retryDelayMs,
              userID: userIDRef.current || 'unknown'
            },
            level: 'warning',
            message: 'Stream queryChannels received HTTP 504, retrying'
          })

          await sleep(retryDelayMs)
        }
      }
    }) as StreamChat['queryChannels']

    chatClientService.queryChannels = wrappedQueryChannels

    return () => {
      if (!originalQueryChannelsRef.current) return
      chatClientService.queryChannels = originalQueryChannelsRef.current
      originalQueryChannelsRef.current = null
    }
  }, [chatClientService, telemetry])

  const contextValue: IChatContext = useMemo(
    () => ({
      chatClient: chatClientService,
      activeChatID,
      onSetActiveChat,
      connectionStatus,
      currentUser,
      disconnectUser,
      hasResolvedInitialInboxChannelQuery,
      isLoading,
      mentionableUsers,
      searchUser,
      teamMembers,
      unreadCount,
      userConnection,
      workspaceUsers: []
    }),
    [
      activeChatID,
      chatClientService,
      connectionStatus,
      currentUser,
      disconnectUser,
      hasResolvedInitialInboxChannelQuery,
      isLoading,
      mentionableUsers,
      onSetActiveChat,
      searchUser,
      teamMembers,
      unreadCount,
      userConnection
    ]
  )

  return <ChatContextProvider value={contextValue}>{children}</ChatContextProvider>
}

export { ChatContextProvider, ChatProvider, useChat }
